import {
  TeContainer,
  TeContainerSlim,
  TeModal,
  TeSegment,
  TeSegmentText,
} from '@topcon/tuicl-react';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';

import { Section } from '../../../utils/styling';
import {
  CustomerBody,
  RequestBusinessPartnerEndCustomerTypeEnum,
  ServiceAgreementTermsEnum,
} from '../../../api/generated/pm';
import CustomerForm from '../../forms/CustomerForm';
import { AgreementCreationType, StepEnum, StepKey } from '../../../types/customer-modal.types';
import { CreateCustomerStepActions } from './CreateCustomerStepActions';
import SelectAgreementStep from './SelectAgreementStep';
import {
  ProxyBusinessPartnerFormData,
  useBusinessPartnerForm,
} from '../../../hooks/forms/useBusinessPartnerForm';
import SelectExistingCustomer from './SelectExistingCustomer';
import useServiceAgreement from '../../../hooks/useServiceAgreement';
import ReviewAgreementStep from './ReviewAgreementStep';

const MODAL_WIDTH = 800;

interface Props {
  open: boolean;
  onClose: (shouldUpdate: boolean) => void;
  /**
   * Seeds the customer form's initial field values (e.g. name/address already
   * known from a Customer Suggestion). Only read on mount, so callers that
   * pre-fill must mount this modal fresh per subject.
   */
  defaultValues?: Partial<ProxyBusinessPartnerFormData>;
  /**
   * Offers the "Add existing customer" tab. Off for entry points that already
   * know the customer is new (the suggestion had no acceptable match).
   * Defaults to true.
   */
  allowExistingCustomer?: boolean;
  /** Renders a dismissible "please verify" banner over the pre-filled fields. */
  showVerifyBanner?: boolean;
  /**
   * The id of the Migration Candidate (Customer Suggestion) this request
   * resolves. Set on the built request body as
   * `migrationCandidateBusinessPartnerId` so the backend can link the new
   * customer request back to the suggestion and transition its status.
   * Omitted for ordinary (non-migration) create flows.
   */
  migrationCandidateBusinessPartnerId?: string;
}

function CreateCustomerModal({
  open,
  onClose,
  defaultValues,
  allowExistingCustomer = true,
  showVerifyBanner,
  migrationCandidateBusinessPartnerId,
}: Props) {
  const { t } = useTranslation();
  const [creationType, setCreationType] = useState<AgreementCreationType>(
    AgreementCreationType.New
  );
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerBody | undefined>();
  const [providerCustomerNumber, setProviderCustomerNumber] = useState<string>('');
  const [searchValue, setSearchValue] = useState<string>('');
  const [step, setStep] = useState<StepEnum>(StepEnum.SelectBusinessPartner);
  const currentStep = Object.values(StepEnum)
    .filter((v) => isNaN(+v))
    .findIndex((s) => StepEnum[s as StepKey] === step);
  const [isEulaDisclaimerAccepted, setIsEulaDisclaimerAccepted] = useState<boolean>(false);
  const [isAgreementAcknowledged, setIsAgreementAcknowledged] = useState<boolean>(false);
  const [verifyBannerDismissed, setVerifyBannerDismissed] = useState<boolean>(false);

  const {
    formName,
    isPending,
    handleFormSubmit: createProxyCustomer,
    formFields,
    setFormFields,
    validateForm,
  } = useBusinessPartnerForm({
    businessPartnerType: 'proxy',
    defaultValues: {
      type: RequestBusinessPartnerEndCustomerTypeEnum.END_CUSTOMER_INDIVIDUAL,
      serviceAgreementTerms: ServiceAgreementTermsEnum.FULL_SERVICE,
      ...defaultValues,
    },
    migrationCandidateBusinessPartnerId,
  });

  const { createServiceAgreementWithCustomer, isPendingCreateServiceAgreementWithCustomer } =
    useServiceAgreement();

  const totalSteps = Object.values(StepEnum).filter((v) => isNaN(+v)).length;

  const footerText = t('step.count', { ns: 'common', current: currentStep + 1, total: totalSteps });
  const modalTitle = t('customers.createModal.title', { ns: 'common' });

  const isFormValid = validateForm(formFields);

  const handleSelectAgreement = (agreementType: ServiceAgreementTermsEnum) => {
    setFormFields({
      ...formFields,
      serviceAgreementTerms: agreementType,
    });
  };

  const handleCreateServiceAgreement = async () => {
    if (creationType === AgreementCreationType.New) {
      await createProxyCustomer();
      onClose(true);
    } else if (creationType === AgreementCreationType.Existing) {
      if (!selectedCustomer) return;
      await createServiceAgreementWithCustomer(
        {
          customerBusinessPartnerId: selectedCustomer.id,
          serviceAgreementTerms: formFields.serviceAgreementTerms,
          customerNumber: providerCustomerNumber.trim() || undefined,
        },
        selectedCustomer.name
      );
      onClose(true);
    }
  };

  const isNextStepDisabled =
    (creationType === AgreementCreationType.New ? !isFormValid : !selectedCustomer) ||
    (step === StepEnum.SelectAgreement && !isAgreementAcknowledged) ||
    (step === StepEnum.ReviewAgreement && !isEulaDisclaimerAccepted) ||
    isPendingCreateServiceAgreementWithCustomer;

  return (
    <TeModal
      open={open}
      closeable={!isPending && !isPendingCreateServiceAgreementWithCustomer}
      closeMode="button"
      teclose={() => onClose(false)}
      modalTitle={modalTitle}
      contentPadding={0}
      footerText={footerText}
    >
      <TeContainer
        padding={12}
        width={MODAL_WIDTH}
        column
        grow
        backgroundColor="background.primary"
        gap="xs"
        overflow="auto"
        alignItems="center"
      >
        <Section>
          <TeContainerSlim fill grow column justifyContent="center" gap="s">
            {step === StepEnum.SelectBusinessPartner && (
              <>
                {allowExistingCustomer && (
                  <TeSegment grow size="s" variant="secondary">
                    <TeSegmentText
                      text={t('createNewCustomer')}
                      active={creationType === AgreementCreationType.New}
                      teclick={() => setCreationType(AgreementCreationType.New)}
                    />
                    <TeSegmentText
                      text={t('addExistingCustomer')}
                      active={creationType === AgreementCreationType.Existing}
                      teclick={() => setCreationType(AgreementCreationType.Existing)}
                    />
                  </TeSegment>
                )}
                {creationType === AgreementCreationType.New && (
                  <CustomerForm
                    formName={formName}
                    isPending={isPending}
                    handleFormSubmit={createProxyCustomer}
                    formFields={formFields as ProxyBusinessPartnerFormData}
                    setFormFields={setFormFields}
                    showVerifyBanner={showVerifyBanner && !verifyBannerDismissed}
                    onDismissVerifyBanner={() => setVerifyBannerDismissed(true)}
                  />
                )}
                {creationType === AgreementCreationType.Existing && (
                  <SelectExistingCustomer
                    selectedCustomer={selectedCustomer}
                    setSelectedCustomer={setSelectedCustomer}
                    providerCustomerNumber={providerCustomerNumber}
                    setProviderCustomerNumber={setProviderCustomerNumber}
                    searchValue={searchValue}
                    setSearchValue={setSearchValue}
                  />
                )}
              </>
            )}
            {step === StepEnum.SelectAgreement && (
              <SelectAgreementStep
                onSelectAgreement={handleSelectAgreement}
                agreementType={formFields.serviceAgreementTerms}
                onAcknowledgedChange={setIsAgreementAcknowledged}
              />
            )}
            {step === StepEnum.ReviewAgreement && (
              <ReviewAgreementStep onChecked={(value) => setIsEulaDisclaimerAccepted(value)} />
            )}
          </TeContainerSlim>
        </Section>
      </TeContainer>
      <CreateCustomerStepActions
        onClose={onClose}
        isPending={isPending || isPendingCreateServiceAgreementWithCustomer}
        isNextStepDisabled={isNextStepDisabled}
        currentStep={currentStep}
        totalSteps={totalSteps}
        onPreviousStep={() => setStep(currentStep - 1)}
        onNextStep={() => setStep(currentStep + 1)}
        onSubmit={handleCreateServiceAgreement}
        creationType={creationType}
      />
    </TeModal>
  );
}

export default CreateCustomerModal;
